import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Grid, Paper, Tabs, Tab, Typography, Dialog, DialogTitle, DialogContent, IconButton, Button, CircularProgress } from '@mui/material';
import { useMediaQuery, useTheme } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import { CornersOut, CornersIn } from '@phosphor-icons/react';
import { TemplateProvider } from '../../../providers/TemplateProvider';
import { ThemeContext } from 'styled-components';
import { AuthContext } from '../../auth/providers/AuthProvider';
// Lightweight throttle implementation to avoid importing entire lodash
const throttle = (func, wait) => {
    let timeout;
    let previous = 0;
    return function(...args) {
        const now = Date.now();
        const remaining = wait - (now - previous);
        if (remaining <= 0 || remaining > wait) {
            if (timeout) {
                clearTimeout(timeout);
                timeout = null;
            }
            previous = now;
            func.apply(this, args);
        } else if (!timeout) {
            timeout = setTimeout(() => {
                previous = Date.now();
                timeout = null;
                func.apply(this, args);
            }, remaining);
        }
    };
};
import Authorized from '../../../layouts/Authorized';
import TemplateModel from '../models/TemplateModel';
import ParticipantsModel from '../models/ParticipantsModel';
import StatementsModel from '../models/StatementsModel';
import TransactionsModel from '../models/TransactionsModel';
import RulesList from '../components/RulesList';
import SectionHeader from '../components/SectionHeader';
import { useQuery, useQueryClient } from 'react-query';
import { getUsersTemplate } from '../../../services/template/Templates';
import { getBillRules } from '../../../services/bill/BillRules';
import { listTemplateStatements } from '../../../services/template/Statements';
import { listTemplateTransactions } from '../../../services/template/Transactions';
import { CsrfContext } from '../../../providers/CsrfProvider';
import { useGroup } from '../../../providers/GroupProvider';
import WithActions from '../../common/components/Wrappers/WithActions';

const ViewTemplates = () => {
    const mediaTheme = useTheme();
    const theme = useContext(ThemeContext);
    const { authClaims } = useContext(AuthContext);
    const { selectedGroupId, setSelectedGroup } = useGroup();
    const isMediumOrLarger = useMediaQuery(mediaTheme.breakpoints.up('md'));
    const { id } = useParams();
    const { csrfToken } = useContext(CsrfContext);
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState(0);
    // Persist expanded pane so refresh keeps us in page mode on that pane
    const EXPANDED_PANE_STORAGE_KEY = 'templateView_expandedPane';
    const [expandedPaneIndex, setExpandedPaneIndex] = useState(() => {
        try {
            const stored = localStorage.getItem(EXPANDED_PANE_STORAGE_KEY);
            if (stored != null && stored !== '') {
                const n = parseInt(stored, 10);
                if (n >= 0 && n <= 3) return n;
            }
        } catch (e) {}
        return null;
    });

    // Persist expanded pane so refresh keeps page mode and pane
    useEffect(() => {
        try {
            if (expandedPaneIndex === null) {
                localStorage.removeItem(EXPANDED_PANE_STORAGE_KEY);
            } else {
                localStorage.setItem(EXPANDED_PANE_STORAGE_KEY, String(expandedPaneIndex));
            }
        } catch (e) {}
    }, [expandedPaneIndex]);

    // When user collapses, enable all fetches so pane mode has data. When user expands or switches pane, enable that pane's fetch.
    useEffect(() => {
        if (expandedPaneIndex === null) {
            setParticipantsFetchEnabled(true);
            setRulesFetchEnabled(true);
            setStatementsFetchEnabled(true);
            setTransactionsFetchEnabled(true);
            setStatementsEverInView(true);
            setTransactionsEverInView(true);
            setParticipantsRevealed(true);
            setRulesRevealed(true);
            setStatementsRevealed(true);
            setTransactionsRevealed(true);
        } else {
            switch (expandedPaneIndex) {
                case 0:
                    setParticipantsFetchEnabled(true);
                    setParticipantsRevealed(true);
                    break;
                case 1:
                    setRulesFetchEnabled(true);
                    setRulesRevealed(true);
                    break;
                case 2:
                    setStatementsFetchEnabled(true);
                    setStatementsEverInView(true);
                    setStatementsRevealed(true);
                    break;
                case 3:
                    setTransactionsFetchEnabled(true);
                    setTransactionsEverInView(true);
                    setTransactionsRevealed(true);
                    break;
                default:
                    break;
            }
        }
    }, [expandedPaneIndex]);

    const [settingsOpen, setSettingsOpen] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isArchived, setIsArchived] = useState(false);
    const templateModelRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const sectionRefs = useRef([]);
    const tabsRef = useRef(null);
    const dialogPaperRef = useRef(null);

    // Reveal sequence: Transactions → Statements → Rules → Participants (bottom to top). Also gate data fetch.
    const [transactionsRevealed, setTransactionsRevealed] = useState(false);
    const [statementsRevealed, setStatementsRevealed] = useState(false);
    const [rulesRevealed, setRulesRevealed] = useState(false);
    const [participantsRevealed, setParticipantsRevealed] = useState(false);
    const [transactionsReady, setTransactionsReady] = useState(false);
    const [statementsReady, setStatementsReady] = useState(false);
    const [rulesReady, setRulesReady] = useState(false);
    const [participantsReady, setParticipantsReady] = useState(false);
    const [statementsEverInView, setStatementsEverInView] = useState(false);
    const [transactionsEverInView, setTransactionsEverInView] = useState(false);
    // Gate participants/rules/statements/transactions fetch so we only run when pane is ready (participants/rules on id; statements/transactions on reveal)
    const [participantsFetchEnabled, setParticipantsFetchEnabled] = useState(false);
    const [rulesFetchEnabled, setRulesFetchEnabled] = useState(false);
    const [statementsFetchEnabled, setStatementsFetchEnabled] = useState(false);
    const [transactionsFetchEnabled, setTransactionsFetchEnabled] = useState(false);
    // Measured content height per section for smooth expand animation (0 → content height over 0.5s)
    const [participantsExpandedHeight, setParticipantsExpandedHeight] = useState(0);
    const [rulesExpandedHeight, setRulesExpandedHeight] = useState(0);
    const [statementsExpandedHeight, setStatementsExpandedHeight] = useState(0);
    const [transactionsExpandedHeight, setTransactionsExpandedHeight] = useState(0);
    const participantsContentRef = useRef(null);
    const rulesContentRef = useRef(null);
    const statementsContentRef = useRef(null);
    const transactionsContentRef = useRef(null);
    const transactionsRevealedAtRef = useRef(0);

    // Set to true to enable staggered pane slide-in animation; false = all panes visible immediately
    const ANIMATE_PANE_REVEAL = false;
    const REVEAL_MIN_HEIGHT = 80;
    const [isResetting, setIsResetting] = useState(false);
    const [bootstrapTrigger, setBootstrapTrigger] = useState(0);
    const prevIdRef = useRef(null);
    const prevGroupIdRef = useRef(selectedGroupId);
    const didFullResetOrInitialRef = useRef(false);
    const revealTimeoutsRef = useRef([]);

    // When switching bills: full reset (collapse + staged reveal) only on page load or group change.
    // Same-group bill switch: invalidate queries and set *Ready false so panes show loading briefly; keep panes open.
    // useLayoutEffect so this runs before child useEffects (e.g. ParticipantsModel hydration).
    useLayoutEffect(() => {
        if (!id) {
            prevIdRef.current = null;
            prevGroupIdRef.current = selectedGroupId;
            didFullResetOrInitialRef.current = false;
            return;
        }
        const templateId = String(id);
        const switchingBills = prevIdRef.current != null && prevIdRef.current !== templateId;
        // Only treat as group change when both prev and current are set and different (not when selectedGroupId loads from null)
        const groupChanged = prevGroupIdRef.current != null && selectedGroupId != null && prevGroupIdRef.current !== selectedGroupId;
        const isInitialLoad = prevIdRef.current === null;
        const shouldReset = switchingBills || groupChanged;

        if (isInitialLoad) didFullResetOrInitialRef.current = true;
        prevIdRef.current = templateId;
        prevGroupIdRef.current = selectedGroupId;

        if (!shouldReset) return;

        queryClient.invalidateQueries(['templates', templateId]);
        queryClient.invalidateQueries(['participants', templateId]);
        queryClient.invalidateQueries(['billRules', templateId]);
        queryClient.invalidateQueries(['statements', templateId]);
        queryClient.invalidateQueries(['transactions', templateId]);
        queryClient.invalidateQueries(['billingTracks', templateId]);

        if (groupChanged) {
            didFullResetOrInitialRef.current = true;
            setTransactionsReady(false);
            setStatementsReady(false);
            setRulesReady(false);
            setParticipantsReady(false);
            if (ANIMATE_PANE_REVEAL) {
                setBootstrapTrigger((t) => t + 1);
                setIsResetting(true);
                setTransactionsRevealed(false);
                setStatementsRevealed(false);
                setRulesRevealed(false);
                setParticipantsRevealed(false);
                setTransactionsExpandedHeight(0);
                setStatementsExpandedHeight(0);
                setRulesExpandedHeight(0);
                setParticipantsExpandedHeight(0);
                setStatementsEverInView(false);
                setTransactionsEverInView(false);
                const raf = requestAnimationFrame(() => setIsResetting(false));
                return () => cancelAnimationFrame(raf);
            }
        }

        // Same-group bill switch: keep panes open, only set *Ready false so loading shows until rehydrated
        setTransactionsReady(false);
        setStatementsReady(false);
        setRulesReady(false);
        setParticipantsReady(false);
    }, [id, selectedGroupId]);

    // Bootstrap: enable fetches + staged reveal. When starting in page (expanded) mode, only enable the expanded pane's fetch so refresh only loads that pane's data.
    useEffect(() => {
        if (!id) return;
        if (!didFullResetOrInitialRef.current) return;
        didFullResetOrInitialRef.current = false;
        const rafId = requestAnimationFrame(() => {
            const onlyExpandedPane = typeof expandedPaneIndex === 'number' && expandedPaneIndex >= 0 && expandedPaneIndex <= 3;
            if (onlyExpandedPane) {
                switch (expandedPaneIndex) {
                    case 0:
                        setParticipantsFetchEnabled(true);
                        setParticipantsRevealed(true);
                        break;
                    case 1:
                        setRulesFetchEnabled(true);
                        setRulesRevealed(true);
                        break;
                    case 2:
                        setStatementsFetchEnabled(true);
                        setStatementsEverInView(true);
                        setStatementsRevealed(true);
                        break;
                    case 3:
                        setTransactionsFetchEnabled(true);
                        setTransactionsEverInView(true);
                        setTransactionsRevealed(true);
                        break;
                    default:
                        break;
                }
            } else {
                setParticipantsFetchEnabled(true);
                setRulesFetchEnabled(true);
                setStatementsFetchEnabled(true);
                setTransactionsFetchEnabled(true);
                setStatementsEverInView(true);
                setTransactionsEverInView(true);
                if (ANIMATE_PANE_REVEAL) {
                    const t0 = setTimeout(() => {
                        setTransactionsRevealed(true);
                        setTransactionsExpandedHeight(REVEAL_MIN_HEIGHT);
                    }, 0);
                    const t1 = setTimeout(() => {
                        setStatementsRevealed(true);
                        setStatementsExpandedHeight(REVEAL_MIN_HEIGHT);
                    }, 450);
                    const t2 = setTimeout(() => {
                        setRulesRevealed(true);
                        setRulesExpandedHeight(REVEAL_MIN_HEIGHT);
                    }, 900);
                    const t3 = setTimeout(() => {
                        setParticipantsRevealed(true);
                        setParticipantsExpandedHeight(REVEAL_MIN_HEIGHT);
                    }, 1350);
                    revealTimeoutsRef.current = [t0, t1, t2, t3];
                } else {
                    setTransactionsRevealed(true);
                    setStatementsRevealed(true);
                    setRulesRevealed(true);
                    setParticipantsRevealed(true);
                }
            }
        });
        return () => {
            cancelAnimationFrame(rafId);
            revealTimeoutsRef.current.forEach((tid) => clearTimeout(tid));
            revealTimeoutsRef.current = [];
        };
    }, [id, bootstrapTrigger]);

    // Tab click still marks pane in view (e.g. user switched to template and clicked Statements before reveal)
    useEffect(() => {
        if (activeTab === 2) setStatementsEverInView(true);
        if (activeTab === 3) setTransactionsEverInView(true);
    }, [activeTab]);

    // ResizeObserver: drive expanded height when animation enabled
    useEffect(() => {
        if (!ANIMATE_PANE_REVEAL || !participantsRevealed || !participantsContentRef.current) return;
        const el = participantsContentRef.current;
        const update = () => setParticipantsExpandedHeight(Math.max(el.offsetHeight, el.scrollHeight, 80));
        const ro = new ResizeObserver(update);
        ro.observe(el);
        requestAnimationFrame(update);
        return () => ro.disconnect();
    }, [participantsRevealed]);
    useEffect(() => {
        if (ANIMATE_PANE_REVEAL && !participantsRevealed) setParticipantsExpandedHeight(0);
    }, [participantsRevealed]);

    useEffect(() => {
        if (!ANIMATE_PANE_REVEAL || !rulesRevealed || !rulesContentRef.current) return;
        const el = rulesContentRef.current;
        const update = () => setRulesExpandedHeight(Math.max(el.offsetHeight, el.scrollHeight, 80));
        const ro = new ResizeObserver(update);
        ro.observe(el);
        requestAnimationFrame(update);
        return () => ro.disconnect();
    }, [rulesRevealed]);
    useEffect(() => {
        if (ANIMATE_PANE_REVEAL && !rulesRevealed) setRulesExpandedHeight(0);
    }, [rulesRevealed]);

    useEffect(() => {
        if (!ANIMATE_PANE_REVEAL || !statementsRevealed || !statementsContentRef.current) return;
        const el = statementsContentRef.current;
        const update = () => setStatementsExpandedHeight(Math.max(el.offsetHeight, el.scrollHeight, 80));
        const ro = new ResizeObserver(update);
        ro.observe(el);
        requestAnimationFrame(update);
        return () => ro.disconnect();
    }, [statementsRevealed]);
    useEffect(() => {
        if (ANIMATE_PANE_REVEAL && !statementsRevealed) setStatementsExpandedHeight(0);
    }, [statementsRevealed]);

    useEffect(() => {
        if (ANIMATE_PANE_REVEAL && transactionsRevealed) transactionsRevealedAtRef.current = Date.now();
    }, [transactionsRevealed]);
    useEffect(() => {
        if (!ANIMATE_PANE_REVEAL || !transactionsRevealed || !transactionsContentRef.current) return;
        const el = transactionsContentRef.current;
        const MIN_EXPAND_MS = 400;
        const update = () => {
            const measured = Math.max(el.offsetHeight, el.scrollHeight, 80);
            setTransactionsExpandedHeight((prev) => {
                if (measured <= 80) return Math.max(prev, 80);
                if (Date.now() - transactionsRevealedAtRef.current < MIN_EXPAND_MS) return Math.max(prev, 80);
                return Math.max(measured, prev);
            });
        };
        const ro = new ResizeObserver(update);
        ro.observe(el);
        const t = setTimeout(update, MIN_EXPAND_MS);
        return () => {
            ro.disconnect();
            clearTimeout(t);
        };
    }, [transactionsRevealed]);
    useEffect(() => {
        if (ANIMATE_PANE_REVEAL && !transactionsRevealed) setTransactionsExpandedHeight(0);
    }, [transactionsRevealed]);

    // Re-measure when content becomes ready (animation only)
    useEffect(() => {
        if (!ANIMATE_PANE_REVEAL || !statementsRevealed || !statementsReady || !statementsContentRef.current) return;
        const el = statementsContentRef.current;
        const raf = requestAnimationFrame(() => {
            setStatementsExpandedHeight((prev) => Math.max(el.offsetHeight, el.scrollHeight, prev, 80));
        });
        return () => cancelAnimationFrame(raf);
    }, [statementsRevealed, statementsReady]);
    useEffect(() => {
        if (!ANIMATE_PANE_REVEAL || !rulesRevealed || !rulesReady || !rulesContentRef.current) return;
        const el = rulesContentRef.current;
        const raf = requestAnimationFrame(() => {
            setRulesExpandedHeight((prev) => Math.max(el.offsetHeight, el.scrollHeight, prev, 80));
        });
        return () => cancelAnimationFrame(raf);
    }, [rulesRevealed, rulesReady]);
    useEffect(() => {
        if (!ANIMATE_PANE_REVEAL || !participantsRevealed || !participantsReady || !participantsContentRef.current) return;
        const el = participantsContentRef.current;
        const raf = requestAnimationFrame(() => {
            setParticipantsExpandedHeight((prev) => Math.max(el.offsetHeight, el.scrollHeight, prev, 80));
        });
        return () => cancelAnimationFrame(raf);
    }, [participantsRevealed, participantsReady]);

    // Force re-render when templateModel state updates
    useEffect(() => {
        const checkState = () => {
            if (templateModelRef.current?.hasChanges !== undefined) {
                setHasChanges(templateModelRef.current.hasChanges);
            }
            if (templateModelRef.current?.isLoading !== undefined) {
                setIsLoading(templateModelRef.current.isLoading);
            }
            if (templateModelRef.current?.isArchived !== undefined) {
                setIsArchived(templateModelRef.current.isArchived);
            }
        };
        const interval = setInterval(checkState, 100);
        return () => clearInterval(interval);
    }, []);

    // Fetch template data to get the bill name (no full-view loading: show "Bill" until loaded, loading is per-pane)
    const { data: templateData, isLoading: templateLoading } = useQuery(
        ['templates', String(id)],
        () => getUsersTemplate(String(id), csrfToken),
        {
            enabled: !!id && !!csrfToken,
        }
    );

    // Subscribe to hydration queries so we can set "ready" when data has settled (fallback if onHydrated doesn't fire)
    const templateIdStr = id ? String(id) : '';
    const { data: rulesData, isLoading: rulesLoading, isError: rulesError } = useQuery(
        ['billRules', templateIdStr],
        () => getBillRules(templateIdStr, csrfToken),
        { enabled: !!id && !!csrfToken && templateIdStr !== 'none' && rulesFetchEnabled, refetchOnMount: true, refetchOnWindowFocus: false }
    );
    const { data: statementsData, isLoading: statementsLoading, isError: statementsError } = useQuery(
        ['statements', templateIdStr, { cursor: '' }],
        listTemplateStatements(csrfToken),
        { enabled: !!id && !!csrfToken && statementsEverInView && statementsFetchEnabled, refetchOnMount: false, refetchOnWindowFocus: false }
    );
    const { data: transactionsData, isLoading: transactionsLoading, isError: transactionsError } = useQuery(
        ['transactions', templateIdStr, { cursor: '' }],
        listTemplateTransactions(csrfToken),
        { enabled: !!id && !!csrfToken && transactionsEverInView && transactionsFetchEnabled, refetchOnMount: false, refetchOnWindowFocus: false }
    );

    // Participants: only onParticipantsHydrated from ParticipantsModel (no duplicate query here)
    // Rules: only set rulesReady when rulesFetchEnabled so we don't use cached data before fetch when switching bills
    useEffect(() => {
        if (rulesRevealed && rulesFetchEnabled && !rulesLoading && (rulesData !== undefined || rulesError)) {
            setRulesReady(true);
        }
    }, [rulesRevealed, rulesFetchEnabled, rulesLoading, rulesData, rulesError]);
    // Statements: only set ready when statementsFetchEnabled so we don't use cached data before fetch when switching bills
    useEffect(() => {
        if (statementsRevealed && statementsFetchEnabled && !statementsLoading && (statementsData !== undefined || statementsError)) {
            setStatementsReady(true);
        }
    }, [statementsRevealed, statementsFetchEnabled, statementsLoading, statementsData, statementsError]);
    // Transactions: only set ready when transactionsFetchEnabled so we don't use cached data before fetch when switching bills
    useEffect(() => {
        if (transactionsRevealed && transactionsFetchEnabled && !transactionsLoading && (transactionsData !== undefined || transactionsError)) {
            setTransactionsReady(true);
        }
    }, [transactionsRevealed, transactionsFetchEnabled, transactionsLoading, transactionsData, transactionsError]);

    // Re-measure Transactions when content becomes ready (animation only)
    useEffect(() => {
        if (!ANIMATE_PANE_REVEAL || !transactionsRevealed || !transactionsContentRef.current) return;
        const el = transactionsContentRef.current;
        const MIN_EXPAND_MS = 400;
        const measure = () => {
            if (!el) return;
            const measured = Math.max(el.offsetHeight, el.scrollHeight, 80);
            setTransactionsExpandedHeight((prev) => {
                if (measured <= 80) return Math.max(prev, 80);
                if (Date.now() - transactionsRevealedAtRef.current < MIN_EXPAND_MS) return Math.max(prev, 80);
                return Math.max(measured, prev);
            });
        };
        const t1 = setTimeout(measure, MIN_EXPAND_MS);
        const t2 = setTimeout(measure, MIN_EXPAND_MS + 200);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [transactionsRevealed, transactionsReady, transactionsData]);

    // When "All groups" is selected and user navigates to a specific bill, switch to that bill's group (once per bill)
    const syncedGroupForBillRef = useRef(null);
    useEffect(() => {
        if (selectedGroupId !== 'all' || !templateData || !id) return;
        if (syncedGroupForBillRef.current === id) return;
        syncedGroupForBillRef.current = id;
        const billGroupId = templateData.group_id ?? null;
        setSelectedGroup(billGroupId);
    }, [selectedGroupId, templateData, id, setSelectedGroup]);
    useEffect(() => {
        syncedGroupForBillRef.current = null;
    }, [id]);

    const billName = templateData?.name ?? 'Bill';
    const ownerSub = templateData?.owner_sub || '';
    
    // Check if the current user owns the bill
    const isOwner = authClaims?.user_sub && ownerSub && 
        authClaims.user_sub.toUpperCase() === ownerSub.toUpperCase();

    const sectionTitles = ["Participants", "Rules", "Statements", "Transactions"];
    let dividerBackground = theme.colors.secondary;

    // Function to scroll to a section based on its index
    const scrollToSection = (index) => {
        const section = sectionRefs.current[index];
        if (section && scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            // Account for the paddingTop (40px) to align section with top of viewport
            const topPos = section.offsetTop - container.offsetTop - 40;
            container.scrollTo({
                top: Math.max(0, topPos),
                left: 0,
                behavior: 'smooth'
            });
        }
    };

    // Hydration callbacks: used for loading indicator in each pane (Participants and Rules hydrate in background on mount)
    const onParticipantsHydrated = () => {
        setParticipantsReady(true);
    };
    const onRulesHydrated = () => setRulesReady(true);
    const onStatementsHydrated = () => setStatementsReady(true);
    const onTransactionsHydrated = () => setTransactionsReady(true);

    useEffect(() => {
        const handleScroll = throttle(() => {
            const scrollContainer = scrollContainerRef.current;
            if (!scrollContainer) return;

            const containerScrollPosition = scrollContainer.scrollTop;
            const viewportHeight = scrollContainer.clientHeight;
            const scrollCenter = containerScrollPosition + viewportHeight / 2;

            if (containerScrollPosition <= 10) {
                setActiveTab(0);
                return;
            }

            let bestMatch = null;
            sectionRefs.current.forEach((section, index) => {
                if (!section) return;
                const sectionTop = section.offsetTop - scrollContainer.offsetTop;
                const sectionBottom = sectionTop + section.offsetHeight;
                const visibleTop = Math.max(sectionTop, containerScrollPosition);
                const visibleBottom = Math.min(sectionBottom, containerScrollPosition + viewportHeight);
                const visibleHeight = Math.max(0, visibleBottom - visibleTop);
                const visibilityRatio = visibleHeight / section.offsetHeight;
                const isScrollCenterInSection = scrollCenter >= sectionTop && scrollCenter <= sectionBottom;

                if (isScrollCenterInSection) {
                    bestMatch = { index, priority: 2, visibilityRatio };
                } else if (visibilityRatio > 0.3 && (bestMatch === null || bestMatch.priority < 2)) {
                    if (bestMatch === null || visibilityRatio > bestMatch.visibilityRatio) {
                        bestMatch = { index, priority: 1, visibilityRatio };
                    }
                }
            });

            if (bestMatch) {
                setActiveTab(bestMatch.index);
            } else {
                let closestIndex = 0;
                let closestDistance = Infinity;
                sectionRefs.current.forEach((section, index) => {
                    if (!section) return;
                    const sectionTop = section.offsetTop - scrollContainer.offsetTop;
                    const distance = Math.abs(sectionTop - containerScrollPosition);
                    if (distance < closestDistance) {
                        closestDistance = distance;
                        closestIndex = index;
                    }
                });
                setActiveTab(closestIndex);
            }
        }, 100);

        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            handleScroll();
        }
        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    const assignRef = (section, index) => {
        sectionRefs.current[index] = section;
    };

    // Function to scroll the selected tab into view
    const centerSelectedTab = (tabIndex) => {
        if (!tabsRef.current) return;

        // Find all tab buttons
        const tabButtons = tabsRef.current.querySelectorAll('.MuiTab-root');
        if (!tabButtons || tabButtons.length === 0) return;

        const selectedTab = tabButtons[tabIndex];
        if (!selectedTab) return;

        // Scroll the tab into view
        selectedTab.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
        });
    };

    return (
        <>
            <Authorized>
                <TemplateProvider key={id} participantsFetchEnabled={participantsFetchEnabled}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            position: 'fixed',
                            overflow: 'hidden',
                            backgroundColor: theme.colors.backgroundSecondary,
                        }}
                    >
                        <Box
                            sx={{
                                // Lateral
                                width: { xs: '100%', sm: '75%', md: 'min(80%, calc(100% - 280px))' },
                                maxWidth: { xs: '100%', sm: '800px', md: 'min(800px, calc(100% - 280px))' },
                                left: { xs: '50%', sm:'54.5%', md: '53%'},
                                transform: 'translateX(-50%)',

                                top: { xs: '5vw', sm: '5vw' },
                                bottom: { xs: '5vw', sm: '0' },
                                height: { xs: 'calc(85vh - 64px)', sm: '85vh' },

                                margin: 'auto',
                                paddingLeft: { xs: 0, sm: '10px' },
                                paddingRight: { xs: 0, sm: '10px' },
                                position: 'fixed',
                                boxShadow: 20,
                                borderRadius: { xs: 0, sm: 4 },
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: theme.colors.backgroundPrimary,
                                boxSizing: 'border-box',
                                overflow: 'hidden', // Already has this, but ensure it clips rounded corners
                            }}
                        >
                            {/* Bill Name Header with Settings Button; show "Bill" until name loads; no bill-level loading icon (loading is per-pane) */}
                            <SectionHeader
                                title={billName}
                                actionButton={isOwner ? <SettingsIcon /> : null}
                                onActionClick={isOwner ? (e) => {
                                    e?.stopPropagation?.();
                                    setSettingsOpen(true);
                                } : undefined}
                            />
                            <Tabs
                                ref={tabsRef}
                                value={expandedPaneIndex !== null ? expandedPaneIndex : activeTab}
                                onChange={(e, newValue) => {
                                    setActiveTab(newValue);
                                    // When expanded, switching tabs shows that pane's content while staying expanded
                                    setExpandedPaneIndex(prev => prev !== null ? newValue : prev);
                                    if (expandedPaneIndex === null) {
                                        scrollToSection(newValue);
                                        requestAnimationFrame(() => {
                                            requestAnimationFrame(() => {
                                                centerSelectedTab(newValue);
                                            });
                                        });
                                    }
                                }}
                                variant="scrollable"
                                scrollButtons="auto"
                                allowScrollButtonsMobile
                                indicatorColor="secondary" // Color of the tab indicator
                                textColor="inherit" // Color of the text in the tabs
                                sx={{
                                    '.MuiTabs-flexContainer': {
                                        justifyContent: 'space-between', // Center and evenly space tabs
                                    },
                                    '.MuiTab-root': {
                                        flex: 1,  // Evenly spaced flexible cells
                                        minWidth: '100px', // Minimum width - triggers overflow when container is too narrow
                                        maxWidth: 'none',  // No max width constraint
                                        padding: '6px 12px', // Padding around text in each tab
                                        backgroundColor: 'transparent !important',
                                        transition: 'opacity 120ms ease, background-color 0ms !important',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        '&:hover, &:active, &:focus, &.Mui-focusVisible': {
                                            backgroundColor: 'transparent !important',
                                            transition: 'opacity 120ms ease, background-color 0ms !important',
                                        },
                                        '&.Mui-selected': {
                                            fontSize: '15px', // Font size when selected
                                            fontWeight: 'bold', // Optional: make text bold when selected
                                            backgroundColor: 'transparent !important',
                                        },
                                        '& .MuiTouchRipple-root': {
                                            display: 'none !important',
                                        },
                                        '& .MuiTouchRipple-ripple': {
                                            display: 'none !important',
                                        }
                                    }
                                }}
                            >
                                {sectionTitles.map((title, index) => (
                                    <Tab sx={{ color:theme.colors.text}} key={index} label={title} />
                                ))}
                            </Tabs>
                            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', marginRight: { xs: 0, sm: '-5px' }, boxSizing: 'border-box', overflow: 'hidden' }}>
                                {expandedPaneIndex !== null ? (
                                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', paddingTop: '40px', paddingLeft: { xs: 0, sm: '10px' }, paddingRight: { xs: 0, sm: '10px' }, boxSizing: 'border-box' }}>
                                        <Box sx={{ pt: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                            {expandedPaneIndex === 0 && id && (
                                                <ParticipantsModel
                                                    templateId={id}
                                                    enabled={participantsFetchEnabled}
                                                    hydrating={!participantsReady}
                                                    onHydrated={onParticipantsHydrated}
                                                    expandedView
                                                    headerAction={
                                                        <IconButton
                                                            onClick={() => setExpandedPaneIndex(null)}
                                                            sx={{ color: theme.colors.text, '&:hover': { backgroundColor: theme.colors.overlayHover || 'rgba(0,0,0,0.04)' } }}
                                                            size="small"
                                                            aria-label="Collapse pane"
                                                        >
                                                            <CornersIn size={18} />
                                                        </IconButton>
                                                    }
                                                />
                                            )}
                                            {expandedPaneIndex === 1 && id && (
                                                <RulesList
                                                    templateId={id}
                                                    enabled={rulesFetchEnabled}
                                                    hydrating={!rulesReady}
                                                    onHydrated={onRulesHydrated}
                                                    expandedView
                                                    headerAction={
                                                        <IconButton
                                                            onClick={() => setExpandedPaneIndex(null)}
                                                            sx={{ color: theme.colors.text, '&:hover': { backgroundColor: theme.colors.overlayHover || 'rgba(0,0,0,0.04)' } }}
                                                            size="small"
                                                            aria-label="Collapse pane"
                                                        >
                                                            <CornersIn size={18} />
                                                        </IconButton>
                                                    }
                                                />
                                            )}
                                            {expandedPaneIndex === 2 && id && (
                                                <StatementsModel
                                                    templateId={id}
                                                    enabled={statementsEverInView && statementsFetchEnabled}
                                                    hydrating={!statementsReady}
                                                    onHydrated={onStatementsHydrated}
                                                    expandedView
                                                    headerAction={
                                                        <IconButton
                                                            onClick={() => setExpandedPaneIndex(null)}
                                                            sx={{ color: theme.colors.text, '&:hover': { backgroundColor: theme.colors.overlayHover || 'rgba(0,0,0,0.04)' } }}
                                                            size="small"
                                                            aria-label="Collapse pane"
                                                        >
                                                            <CornersIn size={18} />
                                                        </IconButton>
                                                    }
                                                />
                                            )}
                                            {expandedPaneIndex === 3 && id && (
                                                <TransactionsModel
                                                    templateId={id}
                                                    enabled={transactionsEverInView && transactionsFetchEnabled}
                                                    hydrating={!transactionsReady}
                                                    onHydrated={onTransactionsHydrated}
                                                    expandedView
                                                    headerAction={
                                                        <IconButton
                                                            onClick={() => setExpandedPaneIndex(null)}
                                                            sx={{ color: theme.colors.text, '&:hover': { backgroundColor: theme.colors.overlayHover || 'rgba(0,0,0,0.04)' } }}
                                                            size="small"
                                                            aria-label="Collapse pane"
                                                        >
                                                            <CornersIn size={18} />
                                                        </IconButton>
                                                    }
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                ) : (
                                <Box
                                    ref={scrollContainerRef}
                                    sx={{
                                        overflow: 'auto',
                                        flex: 1,
                                        minHeight: 0,
                                        paddingRight: { xs: 0, sm: '5px' }, // Space for scrollbar
                                        paddingTop: '40px', // Space between tabs and content
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: `${theme.colors.brandSecondary} !important`,
                                        },
                                        scrollbarColor: `${theme.colors.brandSecondary} transparent`, /* Firefox */
                                    }}
                                >
                                        <Box
                                            key="section-participants"
                                            ref={(el) => assignRef(el, 0)}
                                            data-title={"Participants"}
                                            sx={{
                                                position: 'relative',
                                                overflow: 'hidden',
                                                maxHeight: participantsRevealed ? (ANIMATE_PANE_REVEAL ? participantsExpandedHeight : 'none') : 0,
                                                transition: isResetting ? 'none' : (ANIMATE_PANE_REVEAL ? 'max-height 0.5s ease-out' : 'none'),
                                                boxSizing: 'border-box',
                                                width: '100%',
                                                maxWidth: '100%',
                                            }}>
                                            <Box ref={participantsContentRef} sx={{ paddingLeft: { xs: 0, sm: '10px' }, paddingRight: { xs: 0, sm: '10px' }, boxSizing: 'border-box', width: '100%', minHeight: participantsRevealed ? (participantsReady ? 80 : 120) : 0 }}>
                                                {id && (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                                        <ParticipantsModel
                                                            templateId={id}
                                                            enabled={participantsFetchEnabled}
                                                            hydrating={!participantsReady}
                                                            onHydrated={onParticipantsHydrated}
                                                            headerAction={
                                                                <IconButton
                                                                    onClick={() => setExpandedPaneIndex(0)}
                                                                    sx={{ color: theme.colors.text, '&:hover': { backgroundColor: theme.colors.overlayHover || 'rgba(0,0,0,0.04)' } }}
                                                                    size="small"
                                                                    aria-label="Expand pane"
                                                                >
                                                                    <CornersOut size={18} />
                                                                </IconButton>
                                                            }
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                <Box
                                    key="section-rules"
                                    ref={(el) => assignRef(el, 1)}
                                    data-title={"Rules"}
                                    sx={{
                                        position: 'relative',
                                        overflow: 'hidden',
                                        maxHeight: rulesRevealed ? (ANIMATE_PANE_REVEAL ? rulesExpandedHeight : 'none') : 0,
                                        transition: isResetting ? 'none' : (ANIMATE_PANE_REVEAL ? 'max-height 0.5s ease-out' : 'none'),
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        maxWidth: '100%',
                                    }}>
                                    <Box ref={rulesContentRef} sx={{ paddingLeft: { xs: 0, sm: '10px' }, paddingRight: { xs: 0, sm: '10px' }, boxSizing: 'border-box', width: '100%', minHeight: rulesRevealed ? (rulesReady ? 80 : 120) : 0 }}>
                                        {id && (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                                <RulesList
                                                    templateId={id}
                                                    enabled={rulesFetchEnabled}
                                                    hydrating={!rulesReady}
                                                    onHydrated={onRulesHydrated}
                                                    headerAction={
                                                        <IconButton
                                                            onClick={() => setExpandedPaneIndex(1)}
                                                            sx={{ color: theme.colors.text, '&:hover': { backgroundColor: theme.colors.overlayHover || 'rgba(0,0,0,0.04)' } }}
                                                            size="small"
                                                            aria-label="Expand pane"
                                                        >
                                                            <CornersOut size={18} />
                                                        </IconButton>
                                                    }
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                                <Box
                                    key="section-statements"
                                    ref={(el) => assignRef(el, 2)}
                                    data-title={"Statements"}
                                    sx={{
                                        position: 'relative',
                                        overflow: 'hidden',
                                        maxHeight: statementsRevealed ? (ANIMATE_PANE_REVEAL ? statementsExpandedHeight : 'none') : 0,
                                        transition: isResetting ? 'none' : (ANIMATE_PANE_REVEAL ? 'max-height 0.5s ease-out' : 'none'),
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        maxWidth: '100%',
                                    }}>
                                    <Box ref={statementsContentRef} sx={{ paddingLeft: { xs: 0, sm: '10px' }, paddingRight: { xs: 0, sm: '10px' }, boxSizing: 'border-box', width: '100%', minHeight: statementsRevealed ? (statementsReady ? 80 : 120) : 0 }}>
                                        {statementsRevealed && (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                                <StatementsModel
                                                    templateId={id}
                                                    enabled={statementsEverInView && statementsFetchEnabled}
                                                    hydrating={!statementsReady}
                                                    onHydrated={onStatementsHydrated}
                                                    headerAction={
                                                        <IconButton
                                                            onClick={() => setExpandedPaneIndex(2)}
                                                            sx={{ color: theme.colors.text, '&:hover': { backgroundColor: theme.colors.overlayHover || 'rgba(0,0,0,0.04)' } }}
                                                            size="small"
                                                            aria-label="Expand pane"
                                                        >
                                                            <CornersOut size={18} />
                                                        </IconButton>
                                                    }
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                                <Box
                                    key="section-transactions"
                                    ref={(el) => assignRef(el, 3)}
                                    data-title={"Transactions"}
                                    sx={{
                                        position: 'relative',
                                        overflow: 'hidden',
                                        maxHeight: transactionsRevealed ? (ANIMATE_PANE_REVEAL ? transactionsExpandedHeight : 'none') : 0,
                                        transition: isResetting ? 'none' : (ANIMATE_PANE_REVEAL ? 'max-height 0.5s ease-out' : 'none'),
                                        boxSizing: 'border-box',
                                        width: '100%',
                                        maxWidth: '100%',
                                    }}>
                                    <Box ref={transactionsContentRef} sx={{ paddingLeft: { xs: 0, sm: '10px' }, paddingRight: { xs: 0, sm: '10px' }, boxSizing: 'border-box', width: '100%', minHeight: transactionsRevealed ? (transactionsReady ? 80 : 120) : 0 }}>
                                        {transactionsRevealed && (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                                <TransactionsModel
                                                    templateId={id}
                                                    enabled={transactionsEverInView && transactionsFetchEnabled}
                                                    hydrating={!transactionsReady}
                                                    onHydrated={onTransactionsHydrated}
                                                    headerAction={
                                                        <IconButton
                                                            onClick={() => setExpandedPaneIndex(3)}
                                                            sx={{ color: theme.colors.text, '&:hover': { backgroundColor: theme.colors.overlayHover || 'rgba(0,0,0,0.04)' } }}
                                                            size="small"
                                                            aria-label="Expand pane"
                                                        >
                                                            <CornersOut size={18} />
                                                        </IconButton>
                                                    }
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                                </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Settings Modal */}
                        <Dialog
                            open={settingsOpen}
                            onClose={() => setSettingsOpen(false)}
                            maxWidth={false}
                            fullWidth
                            disableEnforceFocus
                            PaperProps={{
                                ref: dialogPaperRef,
                                sx: {
                                    backgroundColor: theme.colors.backgroundPrimary,
                                    color: theme.colors.text,
                                    borderRadius: '16px',
                                    maxWidth: { xs: '95vw', sm: '600px' },
                                    width: { xs: '95vw', sm: '70vw' },
                                }
                            }}
                        >
                            <DialogTitle sx={{ padding: 0 }}>
                                <SectionHeader
                                    title="Settings"
                                    borderRadius="16px 16px 0 0"
                                    margin={0}
                                />
                            </DialogTitle>
                            <DialogContent sx={{
                                paddingTop: 3,
                                paddingLeft: 0,
                                paddingRight: 0,
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%',
                                }}>
                                    <TemplateModel
                                        id={id}
                                        hideButtons={true}
                                        onSaveSuccess={() => setSettingsOpen(false)}
                                        onArchiveSuccess={() => setSettingsOpen(false)}
                                        onArchiveRef={templateModelRef}
                                        dialogPaperRef={dialogPaperRef}
                                    />
                                </Box>
                            </DialogContent>
                            <Box sx={{
                                paddingTop: 3,
                                paddingBottom: 3,
                                paddingLeft: 0,
                                paddingRight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}>
                                <WithActions isButton={true} forceSpacing>
                                    <Box sx={{
                                        display: 'flex',
                                        gap: 2,
                                        width: '100%',
                                    }}>
                                        <Button
                                            variant="outlined"
                                            color="warning"
                                            onClick={() => {
                                                if (templateModelRef.current) {
                                                    templateModelRef.current.handleArchive();
                                                }
                                            }}
                                            disabled={isLoading}
                                            sx={{
                                                flex: 1,
                                                borderRadius: '8px',
                                            }}
                                        >
                                            {isLoading 
                                                ? (isArchived ? 'Unarchiving...' : 'Archiving...') 
                                                : (isArchived ? 'Unarchive' : 'Archive')}
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={() => {
                                                if (hasChanges) {
                                                    // Save
                                                    if (templateModelRef.current?.handleSubmit) {
                                                        templateModelRef.current.handleSubmit();
                                                    } else {
                                                        // Fallback: try to find and submit form
                                                        const dialogContent = dialogPaperRef?.current;
                                                        const form = dialogContent 
                                                            ? dialogContent.querySelector('form')
                                                            : document.querySelector('form');
                                                        if (form) {
                                                            form.requestSubmit();
                                                        }
                                                    }
                                                } else {
                                                    // Cancel
                                                    setSettingsOpen(false);
                                                }
                                            }}
                                            disabled={isLoading}
                                            sx={{
                                                flex: 1,
                                                borderRadius: '8px',
                                                border: hasChanges 
                                                    ? `1px solid ${theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary}`
                                                    : `1px solid ${theme.colors.border || '#ccc'}`,
                                                backgroundColor: 'transparent',
                                                color: hasChanges 
                                                    ? theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary
                                                    : theme.colors.text,
                                                '&:hover': {
                                                    borderColor: hasChanges 
                                                        ? theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary
                                                        : theme.colors.border || '#ccc',
                                                    backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                },
                                            }}
                                        >
                                            {templateModelRef.current?.isLoading 
                                                ? 'Saving...' 
                                                : hasChanges 
                                                    ? 'Save' 
                                                    : 'Cancel'}
                                        </Button>
                                    </Box>
                                </WithActions>
                            </Box>
                        </Dialog>
                    </Box>
                </TemplateProvider>
            </Authorized>
        </>
    );
};

export default ViewTemplates;
